import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { Button, Input, Message, Stack, toaster } from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';
import CloseIcon from '@rsuite/icons/Close';

import { ensureLeafletDefaultIcon } from '../utils/leafletIcons';
import { geocodeCity, reverseGeocode } from '../utils/locationLookup';
import './ProjectLocationPicker.css';

ensureLeafletDefaultIcon();

const DEFAULT_CENTER = [37, -96];
const FOCUSED_PICKER_ZOOM = 3;
const US_BOUNDS = L.latLngBounds(
  [24.396308, -124.848974],
  [49.384358, -66.885444]
);
const DEFAULT_US_ZOOM_IN_STEPS = 1;

const isFiniteCoordinate = (value) => Number.isFinite(Number(value));
const hasMeaningfulCoordinates = (lat, lng) => {
  if (!isFiniteCoordinate(lat) || !isFiniteCoordinate(lng)) return false;
  const latN = Number(lat);
  const lngN = Number(lng);
  return !(Math.abs(latN) < 0.000001 && Math.abs(lngN) < 0.000001);
};

const applyDefaultUsView = (map) => {
  map.fitBounds(US_BOUNDS, {
    padding: [0, 0],
    animate: false,
  });

  const z = Number(map.getZoom());
  if (Number.isFinite(z)) {
    map.setZoom(z + DEFAULT_US_ZOOM_IN_STEPS, { animate: false });
  }
};

// Confirmed lookups are remembered in the browser so the tick survives a reload
// and people do not read a remount as "it came unconfirmed". This is a display
// hint only — nothing here is authoritative, and losing it costs nothing.
//
// Entries are keyed by the city text *and* the coordinates it resolved to, so a
// signature stops matching the moment either one changes. That also means no
// project id is needed to scope it.
const CONFIRMED_STORAGE_KEY = 'ppc.location.confirmed';
const CONFIRMED_LIMIT = 50;

const locationSignature = (cityText, lat, lng) => {
  const c = String(cityText || '').trim().toLowerCase();
  const la = Number(lat);
  const ln = Number(lng);
  if (!c || !Number.isFinite(la) || !Number.isFinite(ln)) return null;
  return `${c}|${la.toFixed(5)}|${ln.toFixed(5)}`;
};

const readConfirmedSignatures = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CONFIRMED_STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

const writeConfirmedSignatures = (list) => {
  try {
    window.localStorage.setItem(CONFIRMED_STORAGE_KEY, JSON.stringify(list.slice(0, CONFIRMED_LIMIT)));
  } catch {
    // private mode or quota — the tick just will not persist
  }
};

export default function ProjectLocationPicker({
  city,
  latitude,
  longitude,
  onCityChange,
  onCoordinatesChange,
  disabled = false,
}) {
  const [mapOpen, setMapOpen] = useState(false);
  const [tempLat, setTempLat] = useState(null);
  const [tempLng, setTempLng] = useState(null);
  const [isFindingCity, setIsFindingCity] = useState(false);
  const [isApplyingMapSelection, setIsApplyingMapSelection] = useState(false);
  const [errorText, setErrorText] = useState('');
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const latestCenterRef = useRef(DEFAULT_CENTER);
  const latestHasSavedCoordinatesRef = useRef(false);
  const latestHasTempCoordinatesRef = useRef(false);
  const latestTempLatRef = useRef(null);
  const latestTempLngRef = useRef(null);

  const hasSavedCoordinates = hasMeaningfulCoordinates(latitude, longitude);
  const hasTempCoordinates = hasMeaningfulCoordinates(tempLat, tempLng);

  const currentCenter = useMemo(() => {
    if (hasTempCoordinates) return [Number(tempLat), Number(tempLng)];
    if (hasSavedCoordinates) return [Number(latitude), Number(longitude)];
    return DEFAULT_CENTER;
  }, [hasSavedCoordinates, hasTempCoordinates, latitude, longitude, tempLat, tempLng]);

  useEffect(() => {
    latestCenterRef.current = currentCenter;
    latestHasSavedCoordinatesRef.current = hasSavedCoordinates;
    latestHasTempCoordinatesRef.current = hasTempCoordinates;
    latestTempLatRef.current = tempLat;
    latestTempLngRef.current = tempLng;
  }, [currentCenter, hasSavedCoordinates, hasTempCoordinates, tempLat, tempLng]);

  useEffect(() => {
    if (!mapOpen) return;
    if (!mapContainerRef.current) return;

    const map = L.map(mapContainerRef.current, {
      zoomControl: true,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      referrerPolicy: 'strict-origin-when-cross-origin',
    }).addTo(map);

    map.on('click', (event) => {
      const lat = Number(event?.latlng?.lat);
      const lng = Number(event?.latlng?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      setTempLat(lat);
      setTempLng(lng);
      setErrorText('');
    });

    mapRef.current = map;
    if (latestHasTempCoordinatesRef.current || latestHasSavedCoordinatesRef.current) {
      map.setView(latestCenterRef.current, FOCUSED_PICKER_ZOOM, { animate: false });
    } else {
      applyDefaultUsView(map);
    }

    if (latestHasTempCoordinatesRef.current) {
      markerRef.current = L.marker([
        Number(latestTempLatRef.current),
        Number(latestTempLngRef.current),
      ]).addTo(map);
    }

    // Leaflet may initialize before modal layout settles; defer one invalidate pass.
    const invalidateTimer = setTimeout(() => {
      map.invalidateSize();
    }, 0);

    return () => {
      clearTimeout(invalidateTimer);
      map.stop();
      map.off();
      if (markerRef.current && map.hasLayer(markerRef.current)) {
        map.removeLayer(markerRef.current);
      }
      map.remove();
      if (mapRef.current === map) mapRef.current = null;
      markerRef.current = null;
    };
  }, [mapOpen]);

  useEffect(() => {
    if (!mapOpen) return;
    const map = mapRef.current;
    if (!map) return;

    if (hasTempCoordinates || hasSavedCoordinates) {
      map.panTo(currentCenter, { animate: false });
    } else {
      applyDefaultUsView(map);
    }

    if (hasTempCoordinates) {
      const point = [Number(tempLat), Number(tempLng)];
      if (!markerRef.current) {
        markerRef.current = L.marker(point).addTo(map);
      } else {
        markerRef.current.setLatLng(point);
      }
    } else if (markerRef.current) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }
  }, [currentCenter, hasSavedCoordinates, hasTempCoordinates, mapOpen, tempLat, tempLng]);

  const [confirmedSignatures, setConfirmedSignatures] = useState(readConfirmedSignatures);

  const rememberConfirmed = (signature) => {
    if (!signature) return;
    setConfirmedSignatures((prev) => {
      const next = [signature, ...prev.filter((v) => v !== signature)].slice(0, CONFIRMED_LIMIT);
      writeConfirmedSignatures(next);
      return next;
    });
  };

  const forgetConfirmed = (signature) => {
    if (!signature) return;
    setConfirmedSignatures((prev) => {
      const next = prev.filter((v) => v !== signature);
      writeConfirmedSignatures(next);
      return next;
    });
  };

  const handleFindCity = async () => {
    if (disabled || isFindingCity) return;
    setErrorText('');

    const cityText = String(city || '').trim();
    if (!cityText) {
      setErrorText('Enter a city first, then click Confirm location.');
      toaster.push(
        <Message showIcon type="warning" closable>
          City not found. Enter a city name first.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    setIsFindingCity(true);
    try {
      const match = await geocodeCity(cityText);
      if (!match) {
        setErrorText('Could not find that city on the map.');
        toaster.push(
          <Message showIcon type="warning" closable>
            City not found.
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }

      onCoordinatesChange(match.lat, match.lng);
      if (!String(city || '').trim() && match.city) {
        onCityChange(match.city);
      }
      rememberConfirmed(locationSignature(cityText, match.lat, match.lng));
      toaster.push(
        <Message showIcon type="success" closable>
          City found.
        </Message>,
        { placement: 'topEnd' }
      );
    } catch {
      setErrorText('Unable to resolve city location right now.');
      toaster.push(
        <Message showIcon type="warning" closable>
          City not found.
        </Message>,
        { placement: 'topEnd' }
      );
    } finally {
      setIsFindingCity(false);
    }
  };

  const openMap = () => {
    if (disabled) return;
    setErrorText('');
    if (hasSavedCoordinates) {
      setTempLat(Number(latitude));
      setTempLng(Number(longitude));
    } else {
      setTempLat(null);
      setTempLng(null);
    }
    setMapOpen(true);
  };

  const closeMap = () => {
    if (isApplyingMapSelection) return;
    setMapOpen(false);
  };

  const clearPin = () => {
    forgetConfirmed(locationSignature(city, latitude, longitude));
    onCoordinatesChange(null, null);
    onCityChange('');
    setTempLat(null);
    setTempLng(null);
    setErrorText('');
  };

  const applyMapSelection = async () => {
    if (!hasTempCoordinates) {
      setErrorText('Click a point on the map to select a location.');
      return;
    }

    setIsApplyingMapSelection(true);
    setErrorText('');

    try {
      onCoordinatesChange(Number(tempLat), Number(tempLng));

      const reverse = await reverseGeocode(Number(tempLat), Number(tempLng));
      if (reverse?.city) {
        onCityChange(reverse.city);
      } else {
        // Avoid stale city text when user intentionally picks a new point.
        onCityChange('');
      }

      setMapOpen(false);
    } catch {
      setErrorText('Failed to apply map location. Please try again.');
    } finally {
      setIsApplyingMapSelection(false);
    }
  };

  const hasCityText = String(city || '').trim().length > 0;
  // Mirrors the date pickers: the clear affordance lives in the field, and only
  // appears when there is something to clear.
  const canClearLocation = hasCityText || hasSavedCoordinates;
  const currentSignature = locationSignature(city, latitude, longitude);
  const isCityConfirmed = Boolean(currentSignature) && confirmedSignatures.includes(currentSignature);

  return (
    <div className="ppc-location-picker">
      <div className="ppc-location-picker__field">
        <Input
          value={String(city || '')}
          onChange={onCityChange}
          placeholder="Type a city (e.g. Denver, CO)"
          disabled={disabled}
        />
        {canClearLocation && !disabled ? (
          <button
            type="button"
            className="ppc-location-picker__clear"
            onClick={clearPin}
            title="Clear location"
            aria-label="Clear location"
          >
            <CloseIcon />
          </button>
        ) : null}
      </div>

      <Stack spacing={8} wrap style={{ marginTop: 8 }}>
        <Button
          className={`ppc-location-picker__btn ppc-location-picker__btn--sm${
            isCityConfirmed ? ' ppc-location-picker__btn--confirmed' : ''
          }`}
          onClick={() => void handleFindCity()}
          disabled={disabled || isFindingCity || !hasCityText || isCityConfirmed}
          loading={isFindingCity}
        >
          {isCityConfirmed ? (
            <>
              <i className="bi bi-check-lg" aria-hidden="true" />
              City confirmed
            </>
          ) : (
            'Confirm location'
          )}
        </Button>
        <Button className="ppc-location-picker__btn ppc-location-picker__btn--sm" onClick={openMap} disabled={disabled}>
          Select on map
        </Button>
      </Stack>

      {errorText ? (
        <div style={{ marginTop: 8 }}>
          <Message type="warning" showIcon>{errorText}</Message>
        </div>
      ) : null}

      <Modal open={mapOpen} onClose={closeMap} size="md" overflow={false}>
        <Modal.Header>
          <Modal.Title>Select Project Location</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ppc-location-picker__map-help">
            Click the map to place a marker for this project.
          </div>
          <div style={{ width: '100%', height: 340, borderRadius: 8, overflow: 'hidden', border: '1px solid #e6e6e6' }}>
            <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={closeMap} disabled={isApplyingMapSelection}>Cancel</NsiButton>
          <NsiButton variant="primary" onClick={() => void applyMapSelection()} loading={isApplyingMapSelection}>
            Use selected point
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
